import { useEffect, useRef, useState } from "react";
import { Check, Flame, Loader2, Mic, RotateCcw, X } from "lucide-react";
import { api } from "@/lib/api";

// Five of your own corrections, one at a time. Right answers keep the practice flame alight.
export const MiluQuiz = ({ lang = "", onClose }) => {
  const [round, setRound] = useState(null);
  const [snag, setSnag] = useState("");
  const [at, setAt] = useState(0);
  const [picked, setPicked] = useState("");
  const [right, setRight] = useState(0);
  const [done, setDone] = useState(null);
  const [aloud, setAloud] = useState(localStorage.getItem("cml_quiz_aloud") === "1");
  const [hearing, setHearing] = useState(false);
  const [heard, setHeard] = useState(null);
  const gear = useRef({});

  useEffect(() => () => {
    gear.current.stream?.getTracks().forEach((t) => t.stop());
  }, []);

  const deal = () => {
    setRound(null);
    setSnag("");
    setAt(0);
    setPicked("");
    setRight(0);
    setDone(null);
    api.get("/wingman/quiz", { params: lang ? { lang } : {} })
      .then(({ data }) => setRound(data))
      .catch((e) => setSnag(e?.response?.data?.detail
        || "I need a few corrections before I can quiz you."));
  };

  useEffect(deal, [lang]);   // eslint-disable-line react-hooks/exhaustive-deps

  const question = round?.questions?.[at];

  const answer = async (option) => {
    if (picked) return;
    setPicked(option);    const won = option === question.answer;
    const score = right + (won ? 1 : 0);
    setRight(score);
    setTimeout(async () => {
      if (at + 1 < round.questions.length) {
        setPicked("");
        setHeard(null);
        setAt(at + 1);
        return;
      }
      try {
        const { data } = await api.post("/phrasebook/quiz/result",
                                        { correct: score, total: round.questions.length });
        setDone({ score, total: round.questions.length, streak: data.streak });
      } catch {
        setDone({ score, total: round.questions.length, streak: round.streak });
      }
    }, 900);
  };

  // an answer said out loud: whatever MiLu hears is matched against the options
  const sayAnswer = async () => {
    if (picked || hearing || !question) return;
    setHeard(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      gear.current = { stream, recorder };
      const bits = [];
      recorder.ondataavailable = (e) => e.data.size && bits.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setHearing(false);
        try {
          const form = new FormData();
          form.append("audio", new Blob(bits, { type: "audio/webm" }), "answer.webm");
          const { data: said } = await api.post("/wingman/listen", form, { timeout: 60000 });
          const { data } = await api.post("/wingman/quiz/heard", {
            said: said.text || "", options: question.options, answer: question.answer,
          });
          setHeard(data);
          answer(data.right ? question.answer : (data.picked || "—"));
        } catch {
          setHeard({ heard: "", score: 0 });
        }
      };
      recorder.start();
      setHearing(true);
      setTimeout(() => recorder.state === "recording" && recorder.stop(), 5000);
    } catch {
      setHeard({ heard: "", score: 0, denied: true });
      setHearing(false);
    }
  };


  return (
    <div data-testid="milu-quiz"
         className="mx-5 mb-2 shrink-0 rounded-2xl border border-brand/30 bg-panel/80 px-3 py-2.5">
      <div className="flex items-center gap-2">
        <p className="text-[11px] uppercase tracking-widest text-brand">Today's quiz</p>
        {round?.questions && !done && (
          <span className="text-[10px] text-ink-soft">{at + 1} of {round.questions.length}</span>
        )}
        <button
          data-testid="milu-quiz-aloud"
          onClick={() => {
            const on = !aloud;
            setAloud(on);
            localStorage.setItem("cml_quiz_aloud", on ? "1" : "0");
          }}
          title="Answer out loud instead of tapping"
          className={`ms-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] transition-colors ${
            aloud ? "border-brand bg-brand/15 text-brand" : "border-white/15 text-ink-soft"}`}
        >
          <Mic className="h-2.5 w-2.5" /> Out loud
        </button>
        <button data-testid="milu-quiz-close" onClick={onClose} aria-label="Close quiz"
                className="text-ink-soft transition-colors hover:text-ink">
          <X className="h-3 w-3" />
        </button>
      </div>

      {!round && !snag && (
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-ink-soft">
          <Loader2 className="h-3 w-3 animate-spin" /> Picking your corrections…
        </p>
      )}
      {snag && <p data-testid="milu-quiz-empty" className="mt-2 text-[11px] text-ink-soft">{snag}</p>}

      {question && !done && (
        <>
          <p data-testid="milu-quiz-prompt" dir="auto" className="mt-2 text-sm text-ink">
            {question.prompt}
          </p>

          {aloud && (
            <div className="mt-2">
              <button
                data-testid="milu-quiz-say"
                onClick={sayAnswer}
                disabled={hearing || Boolean(picked)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                  hearing ? "border-pink-400 bg-pink-500/15 text-pink-300"
                    : "border-brand/50 text-brand hover:bg-brand/10"}`}
              >
                {hearing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mic className="h-3 w-3" />}
                {hearing ? "Listening…" : "Say the answer"}
              </button>
              {heard && (
                <p data-testid="milu-quiz-heard" dir="auto" className="mt-1 text-[10px] text-ink-soft">
                  {heard.denied ? "I need the microphone to hear you."
                    : heard.heard ? `I heard: “${heard.heard}” · ${heard.score}% match`
                    : "That did not come through — try again."}
                </p>
              )}
            </div>
          )}
          <div className="mt-2 grid gap-1.5">
            {question.options.map((option) => {
              const isAnswer = option === question.answer;
              const tone = !picked ? "border-white/15 text-ink hover:border-brand/50"
                : isAnswer ? "border-emerald-400/60 bg-emerald-500/10 text-emerald-200"
                : option === picked ? "border-destructive/60 bg-destructive/10 text-destructive"
                : "border-white/10 text-ink-soft";
              return (
                <button
                  key={option}
                  data-testid="milu-quiz-option"
                  dir="auto"
                  onClick={() => answer(option)}
                  className={`rounded-xl border px-2.5 py-1.5 text-start text-[11px] transition-colors ${tone}`}
                >
                  {picked && isAnswer && <Check className="me-1 inline h-3 w-3" />}
                  {option}
                </button>
              );
            })}
          </div>
        </>
      )}

      {done && (
        <div data-testid="milu-quiz-done" className="mt-2">
          <p className="text-sm text-ink">
            <span data-testid="milu-quiz-score" className="font-semibold text-brand">
              {done.score}/{done.total}
            </span>{" "}
            {done.score === done.total ? "— every one right." : "— keep them coming."}
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-[11px] text-amber-300">
            <Flame className="h-3 w-3" /> {done.streak} day practice streak
          </p>
          <button
            data-testid="milu-quiz-again"
            onClick={deal}
            className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-brand/50 px-2.5 py-1 text-[11px] text-brand transition-colors hover:bg-brand/10"
          >
            <RotateCcw className="h-3 w-3" /> Another round
          </button>
        </div>
      )}
    </div>
  );
};
